/**
 * Metal swatches on the product card.
 *
 * Hovering a swatch previews that metal's photo; clicking keeps it and points the card's
 * links at that variant, so following the card lands on the metal that is on screen.
 *
 * Delegated from the document rather than wired per card: the carousels are scrollers
 * whose cards are already in the markup, but a card can also arrive later -- section
 * rendering, a filtered collection -- and delegation covers those without a second pass.
 *
 * Loaded as a module so that two sections both asking for it still evaluate it once;
 * a `defer` script would attach every listener twice.
 */

const CARD = '.mj-product-card';
const SWATCH = '[data-mj-swatch]';
const IMAGE = '.mj-product-card__image';

/**
 * The card's own photo, remembered the first time a swatch replaces it. Read from the
 * element rather than written into Liquid: whatever the card rendered with is the truth,
 * including the `sizes` the calling layout chose.
 */
function rememberBase(image) {
  if (image.dataset.mjBaseSrc === undefined) {
    image.dataset.mjBaseSrc = image.getAttribute('src') || '';
    image.dataset.mjBaseSrcset = image.getAttribute('srcset') || '';
  }
}

function showImage(card, src, srcset) {
  const image = card.querySelector(IMAGE);
  if (!image) return;

  rememberBase(image);

  if (src) {
    image.setAttribute('src', src);
    image.setAttribute('srcset', srcset || '');
  } else {
    image.setAttribute('src', image.dataset.mjBaseSrc);
    image.setAttribute('srcset', image.dataset.mjBaseSrcset);
  }
}

/**
 * What the card shows when nothing is being hovered.
 *
 * Read from the card, not from whichever swatch is pressed: a card renders with its
 * default metal already pressed, and taking that as the resting photo would mean hovering
 * a swatch and moving away silently replaced the picture the card arrived with. Only a
 * click writes here, so a hover that is abandoned leaves nothing behind.
 */
function settle(card) {
  showImage(card, card.dataset.mjChosenImage, card.dataset.mjChosenSrcset);
}

function preview(target) {
  const swatch = target?.closest?.(SWATCH);
  if (!swatch) return;

  const card = swatch.closest(CARD);
  if (card) showImage(card, swatch.dataset.image, swatch.dataset.imageSrcset);
}

function release(target) {
  const card = target?.closest?.(SWATCH)?.closest(CARD);
  if (card) settle(card);
}

/*
 * Moving from one swatch to its neighbour fires the leave before the enter, so settling
 * here and previewing there lands on the right photo without either handler needing to
 * know about the other.
 */
document.addEventListener('pointerover', (event) => preview(event.target));
document.addEventListener('pointerout', (event) => release(event.target));

/* The same preview for a keyboard user arriving on the swatch by tab. */
document.addEventListener('focusin', (event) => preview(event.target));
document.addEventListener('focusout', (event) => release(event.target));

document.addEventListener('click', (event) => {
  const swatch = event.target.closest?.(SWATCH);
  if (!swatch) return;

  const card = swatch.closest(CARD);
  if (!card) return;

  for (const other of card.querySelectorAll(SWATCH)) {
    const chosen = other === swatch;
    other.setAttribute('aria-pressed', chosen ? 'true' : 'false');
    other.classList.toggle('is-selected', chosen);
  }

  if (swatch.dataset.image) {
    card.dataset.mjChosenImage = swatch.dataset.image;
    card.dataset.mjChosenSrcset = swatch.dataset.imageSrcset || '';
  } else {
    delete card.dataset.mjChosenImage;
    delete card.dataset.mjChosenSrcset;
  }

  /*
   * Both of the card's links carry the choice, so the picture and the destination cannot
   * disagree. Rebuilt through URL so an existing `variant` is replaced rather than a
   * second one appended.
   */
  const variantId = swatch.dataset.variantId;
  if (variantId) {
    for (const link of card.querySelectorAll('a[href*="/products/"]')) {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('variant', variantId);
      link.setAttribute('href', `${url.pathname}${url.search}`);
    }
  }

  settle(card);
});
